/**
 * Stream batching: patches, feed events, audit entries and module (ext) events that arrive
 * between two renders are applied to the store in one update, patches first, in the order
 * the hub sent them.
 *
 * A store update re-renders every subscribed component, so at 300 machines applying each
 * message on its own kept the main thread busy and the page stopped painting. The batcher
 * applies at most once per interval (leading edge: a lone message on a quiet stream goes in
 * at once). The interval adapts: after each apply it measures what the apply and the render
 * it caused cost, and waits `backoff` times that before the next one (between `minIntervalMs`
 * and `maxIntervalMs`), so a slow device or a huge fleet still leaves most of every second
 * free for input and painting. Timers keep running in a hidden tab, where animation frames
 * stop, so the queue never grows without bound.
 *
 * A gate (lib/routeSwitch.ts) holds applies while a page is opening: an update landing mid-render
 * would make React start the page over. What arrives meanwhile goes in, in one apply, when the
 * gate opens.
 */
import type { AuditEntry, FeedEvent, SseEventMap, StatePatch } from '@orgus/protocol';
import type { LiveBatch } from './store.ts';

export const LIVE_BATCH_MS = 100;
export const LIVE_BATCH_MAX_MS = 1_000;
/** Wait this many times the last apply's cost: about 20 percent of the main thread at most. */
export const LIVE_BATCH_BACKOFF = 4;

export interface LiveBatcher {
  patch(p: StatePatch): void;
  event(e: FeedEvent): void;
  audit(a: AuditEntry): void;
  ext(e: SseEventMap['ext']): void;
  /** Apply whatever is queued now (before a full state replaces everything). */
  flush(): void;
  /** Drop the timer; nothing queued is applied. */
  stop(): void;
  /** Messages waiting. */
  readonly size: number;
  /** The current interval between applies, ms. */
  readonly interval: number;
}

export interface BatcherOptions {
  minIntervalMs?: number;
  maxIntervalMs?: number;
  backoff?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
  /** While `held()`, nothing is applied; the queue goes in when `onRelease` fires. */
  gate?: LiveGate;
}

export interface LiveGate {
  held(): boolean;
  /** Call `fn` once when the hold ends; returns the cancel. */
  onRelease(fn: () => void): () => void;
}

export function createLiveBatcher(apply: (b: LiveBatch) => void, o: BatcherOptions = {}): LiveBatcher {
  const min = o.minIntervalMs ?? LIVE_BATCH_MS;
  const max = Math.max(min, o.maxIntervalMs ?? LIVE_BATCH_MAX_MS);
  const backoff = o.backoff ?? LIVE_BATCH_BACKOFF;
  const now = o.now ?? (() => performance.now());
  const setTimer = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = o.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const empty = (): LiveBatch => ({ patches: [], events: [], audit: [], ext: [] });
  let q: LiveBatch = empty();
  let timer: unknown = null;
  let last = -Infinity;
  let interval = min;
  let stopped = false;
  /** Cancels the wait on the gate, while one is armed. */
  let gated: (() => void) | null = null;

  const size = () => q.patches.length + q.events.length + q.audit.length + q.ext.length;

  const flush = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
    if (!size()) return;
    if (o.gate?.held()) {
      gated ??= o.gate.onRelease(() => {
        gated = null;
        if (!stopped) flush();
      });
      return;
    }
    const b = q;
    q = empty();
    const started = now();
    last = started;
    apply(b);
    // React renders the store change right after this task; a zero timer runs after that render.
    setTimer(() => {
      if (stopped) return;
      interval = Math.min(max, Math.max(min, (now() - started) * backoff));
    }, 0);
  };

  const schedule = () => {
    if (timer != null || gated != null || stopped) return;
    timer = setTimer(flush, Math.max(0, last + interval - now()));
  };

  return {
    patch(p) {
      q.patches.push(p);
      schedule();
    },
    event(e) {
      q.events.push(e);
      schedule();
    },
    audit(a) {
      q.audit.push(a);
      schedule();
    },
    ext(e) {
      q.ext.push(e);
      schedule();
    },
    flush,
    stop() {
      stopped = true;
      if (timer != null) clearTimer(timer);
      timer = null;
      gated?.();
      gated = null;
      q = empty();
    },
    get size() {
      return size();
    },
    get interval() {
      return interval;
    },
  };
}
